import React, { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PublicNavbar } from "@/components/layout/PublicNav";
import { AppFooter } from "@/components/layout/AppFooter";
import { GlowField } from "@/components/ui/GlowField";
import { CtaButton } from "@/components/ui/CtaButton";
import { toast } from "sonner";
import { LogIn, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.full_name}`);
      const target = u.role === "admin" ? "/admin" : u.role === "institute" ? "/institute" : u.role === "parent" ? "/parent" : u.role === "student" ? "/student" : u.role === "staff" ? "/staff" : "/teacher";
      // Priority: ?next= query param → route state → role default
      const next = sp.get("next");
      nav(next || loc.state?.from || target, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFAF7] text-[#0A0C10]">
      <PublicNavbar />
      <div className="flex-1 flex items-center relative overflow-hidden">
        {/* Soft ambient backdrop */}
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full opacity-50" style={{ background: "radial-gradient(circle, rgba(255,180,0,0.35), transparent 60%)" }} />
          <div className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(0,47,167,0.18), transparent 60%)" }} />
        </div>
        <div className="relative w-full max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-center">
          {/* Left visual */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
            className="hidden lg:block"
          >
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#FFB400] inline-flex items-center gap-2 mb-4">
              <span className="w-2 h-2 bg-[#FFB400] inline-block rounded-full" /> Sign in
            </div>
            <h1 className="font-display font-bold text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-[#0A0C10]">
              Pick up<br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#0A0C10] via-[#002FA7] to-[#FFB400]">where you left off.</span>
            </h1>
            <p className="text-neutral-600 mt-5 max-w-md text-[15px] leading-relaxed">
              Your classes, students, AI tools, and weekly digests — already loaded and waiting in the operating console.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-2 max-w-md">
              {["Live classes", "AI tutor", "Paper checker", "Parent portal", "Quiz maker", "Lesson plans"].map((t) => (
                <div key={t} className="text-[11px] font-mono uppercase tracking-[0.16em] text-neutral-500 border border-black/10 bg-white/60 backdrop-blur-sm px-3 py-2 text-center hover:border-[#FFB400] hover:text-[#0A0C10] transition-colors rounded-md">
                  {t}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right card */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.08, ease: [0.2, 0.7, 0.2, 1] }}
            className="bg-white border border-black/8 rounded-2xl shadow-[0_30px_80px_-20px_rgba(10,12,16,0.15)] p-8 sm:p-10 max-w-md w-full mx-auto"
            data-testid="login-card"
          >
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-2">/ sign in</div>
            <h2 className="font-display font-bold text-3xl tracking-tighter">Welcome back.</h2>
            <p className="mt-1.5 text-neutral-600 text-sm">Enter your credentials to open the operating console.</p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <GlowField
                label="Email"
                type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                testid="login-email"
                placeholder="you@school.com"
              />
              <GlowField
                label="Password"
                type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                testid="login-password"
                placeholder="••••••••"
              />
              <CtaButton
                type="submit"
                variant="primary"
                size="lg"
                disabled={loading}
                className="w-full rounded-full"
                data-testid="login-submit"
              >
                <LogIn size={14} /> {loading ? "Signing in…" : "Sign in"}
              </CtaButton>
              <div className="text-right">
                <Link to="/forgot-password" data-testid="forgot-link" className="text-xs font-medium text-neutral-500 hover:text-[#FFB400] transition-colors">Forgot password?</Link>
              </div>
            </form>

            {/* Guest-invite fallback removed — invited students receive the
                teacher's permanent room link which never lands on /login. */}

            <div className="mt-8 pt-6 border-t border-black/8 text-sm text-neutral-600 space-y-1.5">
              <div>New teacher? <Link to="/signup" className="text-[#0A0C10] hover:text-[#FFB400] underline underline-offset-4 decoration-[#FFB400]/50 font-semibold transition-colors">Create teacher account</Link></div>
              {/* Student / Parent / Institute public signups intentionally hidden (pilot phase) */}
            </div>

            <div className="mt-6 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 inline-flex items-center gap-2">
              <Sparkles size={10} className="text-[#FFB400]" /> Secure sign-in · 24/7 AI tutor included
            </div>
          </motion.div>
        </div>
      </div>
      <AppFooter variant="light" />
    </div>
  );
}
